import React from 'react';
import { Form, Button, Card } from 'react-bootstrap';

// Renders a 'table_rows' type form field (e.g. additional_rows on the Chain
// Sling template): a repeatable set of inspection-item rows the inspector can
// add/remove freely, rendered into the certificate's dynamic {%tr for%} table
// starting from item 2 (item 1 is the template's fixed page-1 row - see
// [[project_dynamic_table_rows]]). Stored as a JSON string in formData, same
// as every other field's value, so it flows through handleSubmit/draft-save
// untouched - the value is never a raw array, which matters because
// DynamicForm's submit step auto-joins any array value as a comma-separated
// string for checkbox fields, which would silently corrupt this field.
//
// Fields are stacked vertically (one per line) rather than side-by-side in a
// wide table row - Description, Serial No and Recommendations are all
// textareas since inspectors commonly enter multiple lines, which made a
// horizontal table layout cramped.
const ROW_COLUMNS = [
  { key: 'asset_id_no', label: 'Asset no/ID No', placeholder: 'e.g. N/A', as: 'input' },
  { key: 'serial_no', label: 'Serial No', placeholder: 'Serial number', as: 'textarea', rows: 2 },
  // Description commonly runs to ~10 lines - sized taller so inspectors don't have
  // to scroll inside a tiny box while typing/reviewing it.
  { key: 'description', label: 'Description', placeholder: 'Item description', as: 'textarea', rows: 10 },
  { key: 'swl', label: 'SWL', placeholder: 'e.g. 3.2T', as: 'input' },
  { key: 'recommendations', label: 'Recommendations', placeholder: 'Recommendations', as: 'textarea', rows: 2 },
];

const emptyRow = () => ({ asset_id_no: '', serial_no: '', description: '', swl: '', recommendations: '' });

const parseRows = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value; // guard against a stray raw array ending up here
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const DynamicRowsField = ({ field, value, onChange, disabled }) => {
  const rows = parseRows(value);

  const commitRows = (newRows) => onChange(JSON.stringify(newRows));

  const updateRow = (index, key, newValue) => {
    const newRows = rows.map((row, i) => (i === index ? { ...row, [key]: newValue } : row));
    commitRows(newRows);
  };

  const addRow = () => commitRows([...rows, emptyRow()]);

  const removeRow = (index) => commitRows(rows.filter((_, i) => i !== index));

  return (
    <Form.Group className="mb-3">
      <Form.Label>
        {field.field_label}
        {field.is_required && <span className="text-danger"> *</span>}
      </Form.Label>
      <Form.Text className="text-muted d-block mb-2">
        Item 1 is already covered above. Add a row here for each additional item (item 2, 3, ...).
      </Form.Text>

      {rows.map((row, index) => (
        <Card key={index} className="mb-2">
          <Card.Header className="d-flex justify-content-between align-items-center py-1">
            <span className="fw-bold">Item {index + 2}</span>
            <Button
              variant="link"
              size="sm"
              className="text-danger p-0"
              title="Remove row"
              onClick={() => removeRow(index)}
              disabled={disabled}
            >
              <i className="fas fa-trash-alt me-1"></i>Remove
            </Button>
          </Card.Header>
          <Card.Body className="py-2">
            {ROW_COLUMNS.map((col) => (
              <Form.Group key={col.key} className="mb-2">
                <Form.Label className="small text-muted mb-1">{col.label}</Form.Label>
                <Form.Control
                  as={col.as === 'textarea' ? 'textarea' : 'input'}
                  rows={col.as === 'textarea' ? (col.rows || 2) : undefined}
                  size="sm"
                  placeholder={col.placeholder}
                  value={row[col.key] || ''}
                  onChange={(e) => updateRow(index, col.key, e.target.value)}
                  disabled={disabled}
                />
              </Form.Group>
            ))}
          </Card.Body>
        </Card>
      ))}

      <Button variant="outline-primary" size="sm" onClick={addRow} disabled={disabled}>
        <i className="fas fa-plus me-1"></i>Add Row
      </Button>

      {field.help_text && <Form.Text className="text-muted d-block mt-1">{field.help_text}</Form.Text>}
    </Form.Group>
  );
};

// Read-only display for a 'table_rows' field's stored value - used on
// review/view-only pages where there's no editing control. Same stacked
// layout as the editable version, for the same long-text reason.
export const DynamicRowsPreview = ({ value }) => {
  const rows = parseRows(value);

  if (rows.length === 0) {
    return <span className="text-muted fst-italic">No additional rows</span>;
  }

  return (
    <>
      {rows.map((row, index) => (
        <Card key={index} className="mb-2">
          <Card.Header className="py-1 fw-bold">Item {index + 2}</Card.Header>
          <Card.Body className="py-2">
            {ROW_COLUMNS.map((col) => (
              <div key={col.key} className="mb-1">
                <span className="small text-muted">{col.label}: </span>
                <span style={{ whiteSpace: 'pre-wrap' }}>{row[col.key] || <em className="text-muted">-</em>}</span>
              </div>
            ))}
          </Card.Body>
        </Card>
      ))}
    </>
  );
};

export default DynamicRowsField;
